// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// D-CRX-87 — per-pharmacy feature settings (control plane, mig 446), set from the SaaS-admin CareRx tab.
// E-05: `api/client.ts` is frozen for new exports. Mirrors functions/Features/PharmacyFeatureFunctions.cs (E-19).

import { jsonRequest as req } from './http'
import { apiFetch, parseApiError } from './client'
import { parseWeightUnit, WEIGHT_UNIT_KEY, type WeightUnit } from '../lib/measurements'

export type FeatureType = 'bool' | 'int' | 'date' | 'text'
/** true/false, a whole number, an ISO date (YYYY-MM-DD), the text — or null (not set). */
export type FeatureWireValue = boolean | number | string | null

/** An enum option: the stored value and the words shown for it. */
export interface FeatureOption { value: string; label: string }

export interface FeatureSetting {
  key: string
  type: FeatureType
  label: string
  description: string
  default_value: FeatureWireValue
  /** The value in force: the saved one when overridden, else the default. */
  value: FeatureWireValue
  overridden: boolean
  options: FeatureOption[] | null
  min: number | null
  max: number | null
  updated_at: string | null
  updated_by_name: string | null
  /** D-CRX-87b A6: the pharmacy may also change this itself (CareRx admin). */
  pharmacy_editable?: boolean
}

export interface FeatureSettingsResponse {
  pharmacy_id: string
  area: string
  /** How long a saved change can take to apply; 0 = the next request. */
  cache_seconds: number
  features: FeatureSetting[]
  /** D-CRX-87 §4d: DocuSign is set up for this pharmacy (the send paths' own rule). Older servers omit it. */
  docusign_ready?: boolean
}

export const listCareRxFeatures = (pharmacyId: string) =>
  req<FeatureSettingsResponse>('GET', `/pharmacies/${pharmacyId}/carerx/features`)

/** `null` (or the default value) resets the setting — the server deletes the row. */
export const setCareRxFeature = (pharmacyId: string, key: string, value: FeatureWireValue) =>
  req<FeatureSetting>('PUT', `/pharmacies/${pharmacyId}/carerx/features/${encodeURIComponent(key)}`, { value })

// ── D-CRX-87b A6: the settings a pharmacy may change itself (CareRx admin), pharmacy from the token ──
export interface MyEditableFeaturesResponse { area: string; cache_seconds: number; can_edit: boolean; features: FeatureSetting[] }
export const listMyEditableCareRxFeatures = () => req<MyEditableFeaturesResponse>('GET', '/me/carerx/feature-settings')
/** `null` resets to the default (the row is deleted). Only PharmacyEditable keys; others are 403. */
export const setMyCareRxFeature = (key: string, value: FeatureWireValue) =>
  req<FeatureSetting>('PUT', `/me/carerx/features/${encodeURIComponent(key)}`, { value })

// ── D-CRX-88: the weight unit in force for the signed-in user's pharmacy (GET /me/carerx/features) ──
/** 'lb' (default — today), 'kg' or 'both'. A pharmacy with no feature store yet, or an older server, reads lb. */
export async function getCareRxWeightUnit(): Promise<WeightUnit> {
  const res = await apiFetch('/me/carerx/features')
  if (res.status === 404) return 'lb'
  if (!res.ok) throw await parseApiError(res)
  const body = await res.json() as { features?: Record<string, unknown> }
  return parseWeightUnit(body.features?.[WEIGHT_UNIT_KEY])
}
